import axios, { type AxiosInstance } from 'axios'
import i18n from 'i18n'
import { REACT_APP_SERVER_API } from 'utils/constants/environment'
import { HTTP_STATUS_CODE } from 'utils/constants/http'
import { skipNullParams } from 'utils/helper/common-helpers'
import { buildStaticModeNotFoundPath } from 'utils/helper/static-mode-route'
import { toast } from 'utils/toast'

export type VolunteerSession = {
  volunteerToken: string
  branchId: string
}

let session: VolunteerSession | null = null

export function setVolunteerSession(next: VolunteerSession | null) {
  session = next
}

export function getVolunteerSession(): VolunteerSession | null {
  return session
}

const volunteerRequest: AxiosInstance = axios.create({})

volunteerRequest.interceptors.request.use(
  (config) => {
    config.baseURL = REACT_APP_SERVER_API
    config.headers.set('Accept-Language', i18n.language === 'jp' ? 'ja' : 'en')
    config.params = skipNullParams(config.params)

    if (session?.volunteerToken) {
      config.headers.set('x-volunteer-token', session.volunteerToken)
    }
    if (session?.branchId) {
      config.headers.set('x-branch-id', session.branchId)
    }

    return config
  },
  (error) => Promise.reject(error)
)

volunteerRequest.interceptors.response.use(
  (response) => response,
  (error) => {
    const res = error.response
    const url = String(error.config?.url ?? '')
    const isQrLookup = url.includes('/api/v1/reservation/qr/lookup')
    const skipClientHandledToast =
      isQrLookup &&
      (res?.status === HTTP_STATUS_CODE.BAD_REQUEST ||
        res?.status === HTTP_STATUS_CODE.UNPROCESSABLE_ENTITY)

    if (res?.status === HTTP_STATUS_CODE.FORBIDDEN) {
      const message = res?.data?.message
      const query = message
        ? `?message=${encodeURIComponent(String(message))}`
        : ''

      const volunteerNotFoundPath = buildStaticModeNotFoundPath()

      if (window.location.pathname !== volunteerNotFoundPath) {
        window.location.replace(`${volunteerNotFoundPath}${query}`)
      }
    }

    if (!skipClientHandledToast && res?.status === HTTP_STATUS_CODE.BAD_REQUEST) {
      toast({
        severity: 'error',
        detail: res?.data?.message,
        summary: i18n.t('Error'),
      })
    }

    if (res?.status === HTTP_STATUS_CODE.INTERNAL_SERVER_ERROR) {
      toast({
        severity: 'error',
        detail:
          'エラーが発生しました。しばらく待ってから、再度お試しください。',
        summary: i18n.t('Error'),
      })
    }

    return Promise.reject(error)
  }
)

export default volunteerRequest
